import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { SlideMode } from 'src/constants/constants';
import { setPlayerParams } from 'src/lib/redux';

import { ottPlaylistFetcher } from '../../fetchers/ott-fetcher';
import {
  updateBookmarkedVideos,
  updateOttPlaylistItems,
} from '../../redux/slices/scheduleDetailsSlice/playerMetaSlice';

interface ThumbnailImage {
  _id?: string;
  name?: string;
  baseUrl?: string;
  key?: string;
  type?: string;
  createdAt?: string;
  status?: string;
}

interface Thumbnail {
  portrait?: ThumbnailImage;
  landscape?: ThumbnailImage;
  square?: ThumbnailImage;
}

interface OttVideoDetail {
  _id: string;
  title: string;
  type: string;
  thumbnail?: Thumbnail;
  duration?: number;
  isTrailer?: boolean;
  releaseDate?: string;
  videoId?: string;
  status?: string;
  isLive?: boolean;
  videoType?: string;
  isBookmark?: boolean;
  isReminderSet?: boolean;
  startTime?: string;
  conversationId?: string;
  tag?: string;
  publishAt?: string;
}

export interface PlaylistItem {
  tag: any;
  conversationId: string;
  startTime: string;
  _id: string;
  title: string;
  thumbnail?: string;
  duration?: number;
  status?: 'PUBLISHED' | 'SCHEDULED' | 'playing' | 'completed' | 'upcoming' | 'coming-soon';
  scheduleId?: string;
  videoId?: string;
  isTrailer?: boolean;
  isBookmark?: boolean;
  videoType?: 'penpencilVdo' | 'awsVideo';
  isLive?: boolean;
  releaseDate?: string;
  publishAt?: string;
}

const useOttPlaylist = ({ playlistId, enabled }: { playlistId: string; enabled: boolean }) => {
  const dispatch = useDispatch();

  const { data, isLoading, refetch, isFetched, error } = useQuery(
    [
      'ott-playlist',
      {
        playlistId,
      },
    ],
    ottPlaylistFetcher,
    {
      enabled: enabled && !!playlistId,
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      onSuccess: (data) => {
        const urlParams = new URLSearchParams(window.location.search);
        const currentChildId = urlParams.get('childId');
        // Check if currentChildId exists in the playlist
        const childIdExists = data?.data?.ottVideoDetails?.some(
          (video: OttVideoDetail) => video._id === currentChildId,
        );

        if (!childIdExists) {
          // Current childId not found in playlist, remove playlistId from URL
          urlParams.delete('playlistId');
          window.history.replaceState(
            {},
            '',
            `${window.location.pathname}?${urlParams.toString()}`,
          );
          dispatch(setPlayerParams({ playlistId: '' }));
          return;
        }

        // Update bookmarked videos and playlist items in Redux when API responds
        if (data?.data?.ottVideoDetails && Array.isArray(data.data.ottVideoDetails)) {
          const bookmarks: Record<string, boolean> = {};
          data.data.ottVideoDetails.forEach((video: OttVideoDetail) => {
            bookmarks[video._id] = video.isBookmark || false;
          });
          dispatch(updateBookmarkedVideos({ bookmarkedVideos: bookmarks }));

          // Transform and store playlist items in Redux
          const playlistItems = data.data.ottVideoDetails.map(
            (video: OttVideoDetail): PlaylistItem => {
              const thumbnail = video.thumbnail?.landscape ?? {};

              const thumbnailUrl = `${thumbnail?.baseUrl}${thumbnail.key}`;

              return {
                _id: video._id,
                title: video.title,
                thumbnail: thumbnailUrl,
                duration: video.duration,
                status:
                  video.videoType === 'awsVideo' &&
                  video.isLive === true &&
                  video.releaseDate &&
                  new Date().getTime() >= new Date(video.releaseDate).getTime()
                    ? 'LIVE'
                    : (video.status as any),
                videoId: video.videoId,
                isTrailer: video.isTrailer,
                isBookmark: video.isBookmark,
                videoType: video.videoType as any,
                isLive: video.isLive,
                releaseDate: video.releaseDate,
                conversationId: video.conversationId || '',
                tag: video.tag,
                startTime: video.startTime || '',
                publishAt: video.publishAt,
              };
            },
          );

          // Move SCHEDULED items to end while maintaining order
          const nonScheduled = playlistItems.filter(
            (item: PlaylistItem) => item.status !== 'SCHEDULED',
          );
          const scheduled = playlistItems.filter(
            (item: PlaylistItem) => item.status === 'SCHEDULED',
          );
          const sortedPlaylistItems = [...nonScheduled, ...scheduled];

          dispatch(updateOttPlaylistItems({ ottPlaylistItems: sortedPlaylistItems }));

          // If childId is not present in URL, set first video as childId and reload
          if (!currentChildId && sortedPlaylistItems.length > 0) {
            const firstVideoId = sortedPlaylistItems[0]._id;
            console.log('No childId in URL, setting first video:', firstVideoId);
            urlParams.set('childId', firstVideoId);
            urlParams.set('default_enabled_feature', SlideMode.PLAYLIST);
            window.location.search = urlParams.toString();
          }
        }
      },
    },
  );

  // Transform API response to PlaylistItem format
  const transformedData = useMemo(() => {
    if (!data?.data?.ottVideoDetails || !Array.isArray(data.data.ottVideoDetails)) {
      return [];
    }

    const items = data.data.ottVideoDetails.map((video: OttVideoDetail): PlaylistItem => {
      // Get thumbnail URL (prefer landscape, fallback to portrait or square)
      const thumbnail = video.thumbnail?.landscape ?? {};

      const thumbnailUrl = `${thumbnail?.baseUrl}${thumbnail.key}`;

      return {
        _id: video._id,
        title: video.title || 'Untitled Video',
        thumbnail: thumbnailUrl,
        duration: video.duration || 0,
        status:
          video.videoType === 'awsVideo' &&
          video.isLive === true &&
          video.releaseDate &&
          new Date().getTime() >= new Date(video.releaseDate).getTime()
            ? 'LIVE'
            : (video.status as any),
        scheduleId: video._id,
        videoId: video.videoId,
        isTrailer: video.isTrailer,
        isBookmark: video.isBookmark,
        videoType: video.videoType as any,
        isLive: video.isLive,
        releaseDate: video.releaseDate,
        conversationId: video.conversationId || '',
        tag: video.tag,
        startTime: video.startTime || '',
        publishAt: video.publishAt,
      };
    });

    // Move SCHEDULED items to end while maintaining order
    const nonScheduled = items.filter((item: PlaylistItem) => item.status !== 'SCHEDULED');
    const scheduled = items.filter((item: PlaylistItem) => item.status === 'SCHEDULED');
    return [...nonScheduled, ...scheduled];
  }, [data]);

  return {
    data: transformedData,
    isLoading,
    refetch,
    isFetched,
    error,
  };
};

export default useOttPlaylist;
